"use client";

import { useState, useEffect, useRef } from "react";
import Icon from "../atoms/Icon";

export interface DropdownOption {
  id: string;
  label: string;
}

interface DropdownProps {
  options: DropdownOption[];
  selectedId: string;
  onChange: (id: string) => void;
}

/**
 * @component Dropdown
 * @description A self-contained select dropdown with full keyboard navigation.
 * Renders a trigger button showing the selected option, and a listbox that opens
 * on interaction. When only one option is provided, the trigger is display-only
 * (no chevron, no interaction).
 * All state, refs, and keyboard behaviour are contained within this component.
 * @param options - List of selectable options, each with an `id` and `label`.
 * @param selectedId - The `id` of the currently selected option.
 * @param onChange - Callback fired with the `id` of the newly selected option.
 * @returns A styled dropdown trigger and listbox.
 */
export default function Dropdown({ options, selectedId, onChange }: DropdownProps) {
  const isInteractive = options.length > 1;
  const selectedOption = options.find((o) => o.id === selectedId) ?? options[0];

  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  const openDropdown = (startIndex: number) => {
    setOpen(true);
    setFocusedIndex(startIndex);
  };

  const closeDropdown = () => {
    setOpen(false);
    setFocusedIndex(-1);
    buttonRef.current?.focus();
  };

  // Focus the correct list item when focusedIndex changes
  useEffect(() => {
    if (!open || focusedIndex < 0) return;
    const items = listRef.current?.querySelectorAll<HTMLElement>('[role="option"]');
    items?.[focusedIndex]?.focus();
  }, [focusedIndex, open]);

  // Close when clicking outside
  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeDropdown();
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [open]);

  const selectOption = (id: string) => {
    onChange(id);
    closeDropdown();
  };

  const handleButtonKeyDown = (e: React.KeyboardEvent) => {
    if (!isInteractive) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      openDropdown(open ? Math.min(focusedIndex + 1, options.length - 1) : 0);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      openDropdown(open ? Math.max(focusedIndex - 1, 0) : options.length - 1);
    } else if (e.key === "Escape") {
      closeDropdown();
    }
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
        break;
      case "Home":
        e.preventDefault();
        setFocusedIndex(0);
        break;
      case "End":
        e.preventDefault();
        setFocusedIndex(options.length - 1);
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (focusedIndex >= 0) selectOption(options[focusedIndex].id);
        break;
      case "Escape":
      case "Tab":
        e.preventDefault();
        closeDropdown();
        break;
    }
  };

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        ref={buttonRef}
        className={`w-full min-h-[44px] px-4 py-3 ${isInteractive ? "bg-white shadow-l1" : "bg-mist"}  border border-border-light inline-flex justify-between items-center`}
        aria-haspopup={isInteractive ? "listbox" : undefined}
        aria-expanded={isInteractive ? open : undefined}
        aria-controls={isInteractive ? "dropdown-listbox" : undefined}
        aria-label="Select membership"
        onClick={() => {
          if (!isInteractive) return;
          if (open) {
            closeDropdown();
          } else {
            openDropdown(options.findIndex((o) => o.id === selectedId));
          }
        }}
        onKeyDown={handleButtonKeyDown}
      >
        <p className="text-labels-captions text-charcoal w-full text-left">
          {selectedOption?.label ?? ""}
        </p>
        {isInteractive && (
          <Icon
            name="chevron-down"
            className={`w-4 h-4 text-muted-text transition-transform ${open ? "rotate-180" : ""}`}
          />
        )}
      </button>

      {open && isInteractive && (
        <ul
          ref={listRef}
          id="dropdown-listbox"
          role="listbox"
          aria-label="Options"
          className="absolute z-10 w-full bg-white border border-border-light shadow-l2 mt-1"
          onKeyDown={handleListKeyDown}
        >
          {options.map((option) => (
            <li
              key={option.id}
              role="option"
              aria-selected={option.id === selectedId}
              tabIndex={-1}
              className={`px-4 py-3 text-labels-captions cursor-pointer hover:bg-hover outline-none focus:bg-hover ${
                option.id === selectedId ? "text-charcoal bg-mist" : "text-muted-text"
              }`}
              onClick={() => selectOption(option.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  selectOption(option.id);
                }
              }}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
